import styled, { ThemeProvider } from 'styled-components';
import { css } from '@styled-system/css';
import useSWR, { mutate } from 'swr';
import { useRouter } from 'next/router';
import { Suspense, useEffect, useState } from 'react';

import { LcResourcePreview } from 'humanity/components/lcResourcePreview';
import { theme } from 'utils/theme.v2';
import { learningCenterPageProps } from 'humanity/pages/learningCenterPage/learningCenterPage.types.js';
import { LcTopFilter } from 'humanity/components/lcTopFilter';
import { LcSideFilter } from 'humanity/components/lcSideFilter';
import Section from 'humanity/primitives/section';
import Container from 'humanity/primitives/container';
import { LcMasthead } from 'humanity/sections/lcMasthead';
import Grid from 'humanity/primitives/grid';
import { fetchViaApi } from 'lib/contentful';
import { lcResourcePreviewQuery } from 'humanity/pages/learningCenterPage/learningCenterPage.query';
import { backendToFrontendFilter } from 'utils/learningCenterFilterNames';
import { ITEMS_PER_PAGE } from 'utils/learningCenterConstants';
import { Pagination } from 'humanity/components/pagination';
import { LcTextCard } from 'humanity/components/lcTextCard';
import Loading from 'humanity/components/loading';

const StyledGrid = styled(Grid)(css({ display: ['block', null, null, 'grid'] }));
const StyledSidebar = styled('div')(css({ gridColumnStart: '1', gridColumnEnd: '4' }));
const StyledMainContent = styled('div')(
  css({
    gridColumnStart: '4',
    gridColumnEnd: '13',
    '& > *:not(:first-child)': { marginTop: '40px' },
  })
);
const StyledResourcesGrid = styled(Grid)(
  css({
    gridTemplateColumns: ['1fr', 'repeat(3, 1fr)'],
    gridRowGap: '40px',
  })
);
const fetcher = (query, preview) =>
  fetchViaApi(query, preview).then(
    (entry) => entry?.data?.learningCenterResourceCollection
  );
const LearningCenterPage = ({ pageContent, parentSlug, preview = false }) => {
  const { pathname, query: routerQuery, push } = useRouter();
  const [firstRender, setFirstRender] = useState(true);

  const masthead = pageContent.sectionsCollection?.items.find(
    (item) => item.__typename === 'SectionLearningCenterMasthead2'
  );
  const topFilter = pageContent.sectionsCollection?.items.find(
    (item) => item.__typename === 'ComponentLearningCenterTopFilter'
  );
  const sideFilter = pageContent.sectionsCollection?.items.find(
    (item) => item.__typename === 'ComponentLearningCenterSideFilter'
  );
  const textCard = pageContent.sectionsCollection?.items.find(
    (item) => item.__typename === 'ComponentLearningCenterTextCard'
  );

  const { data } = useSWR(
    lcResourcePreviewQuery(parentSlug, routerQuery, preview),
    (query) => fetcher(query, preview),
    {
      fallbackData: firstRender
        ? pageContent.learningCenterResourceCollection
        : undefined,
      suspense: true,
    }
  );

  const lcResourcePreviews = data?.items;
  const lcResourcePreviewsTotal = data?.total;
  const totalPages = lcResourcePreviewsTotal
    ? Math.ceil(lcResourcePreviewsTotal / ITEMS_PER_PAGE)
    : 1;

  useEffect(() => {
    setFirstRender(false);
  }, [setFirstRender]);

  const handleFilter = (e, filterType) => {
    const { id, checked } = e.target;
    const urlFriendlyFilterType = backendToFrontendFilter(filterType);

    // Get the current filters from the URL
    const currentFilters = routerQuery[urlFriendlyFilterType]
      ? routerQuery[urlFriendlyFilterType].split(',')
      : [];

    // Update the filters
    const newFilters = checked
      ? [...currentFilters, id]
      : currentFilters.filter((filter) => filter !== id);

    const newRouterQuery = { ...routerQuery };

    if (newFilters.length > 0) {
      newRouterQuery[urlFriendlyFilterType] = newFilters.join(',');
      delete newRouterQuery.page;
    } else {
      delete newRouterQuery[urlFriendlyFilterType];
    }
    push(
      {
        pathname,
        query: newRouterQuery,
      },
      undefined,
      { shallow: true }
    );
    const query = lcResourcePreviewQuery(parentSlug, newRouterQuery, preview);
    mutate(query);
  };

  return (
    <ThemeProvider theme={theme}>
      <LcMasthead {...masthead} />
      <Section mb={[9]}>
        <Container>
          <LcTopFilter filters={topFilter.filtersCollection.items} />
          <StyledGrid>
            <StyledSidebar>
              <LcSideFilter
                filterTypes={sideFilter?.filtersCollection?.items}
                handleFilter={handleFilter}
              />
            </StyledSidebar>
            <StyledMainContent>
              <StyledResourcesGrid>
                <Suspense fallback={<Loading />}>
                  {lcResourcePreviews?.length ? (
                    lcResourcePreviews?.map((resource) => (
                      <LcResourcePreview key={resource.sys.id} resource={resource} />
                    ))
                  ) : (
                    <p>No resources found</p>
                  )}
                </Suspense>
              </StyledResourcesGrid>
              {Boolean(lcResourcePreviews?.length) && (
                <Pagination totalPages={totalPages} />
              )}
              {textCard && (
                <LcTextCard title={textCard.title} content={textCard.content} />
              )}
            </StyledMainContent>
          </StyledGrid>
        </Container>
      </Section>
    </ThemeProvider>
  );
};

LearningCenterPage.propTypes = {
  ...learningCenterPageProps,
};

export default LearningCenterPage;
